import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Popover,
  Tooltip,
  Typography
} from "@material-ui/core";
import HelpIcon from "@material-ui/icons/Help";
import { Alert } from "@material-ui/lab";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import bullet from "highcharts/modules/bullet.js";
import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link, withRouter } from "react-router-dom";
import { getQueryParams } from "../../../utils/fns/get-query-params";
import AtSelectPeriod, {
  defaultSelectDateRange,
} from "../../../components/AtSelectPeriod/AtSelectPeriod";
import { ObjectiveSelect } from "../../../components/ObjectiveSelect";
import { useIndicadorApdex } from "../../../hooks/useIndicadorApdex";
import { useObjectivesList } from "../../../hooks/useObjectivesList";
import { ApdexConfigWarning } from "../../../components/Apdex/ApdexConfigWarning";

bullet(Highcharts);

const chartOptions = ({
  umbrales: { sla_error_apdex, sla_ok_apdex },
  apdex_dato,
}) => ({
  credits: {
    enabled: false,
  },
  chart: {
    inverted: true,
    type: "bullet",
    height: 95,
    scrollablePlotArea: {
      minWidth: 400,
      scrollPositionX: 1,
    },
  },
  title: {
    text: null,
  },
  xAxis: {
    categories: [""],
  },
  plotOptions: {
    series: {
      pointPadding: 0.25,
      borderWidth: 0,
      color: "#000",
      targetOptions: {
        width: "200%",
      },
    },
    bullet: {
      pointWidth: 25,
    },
  },
  legend: {
    enabled: false,
  },
  yAxis: {
    max: 1,
    gridLineWidth: 0,
    plotBands: [
      {
        from: 0,
        to: sla_error_apdex ?? 0,
        color: "#e87482",
      },
      {
        from: sla_error_apdex ?? 0,
        to: sla_ok_apdex ?? 0,
        color: "#dbc670",
      },
      {
        from: sla_ok_apdex ?? 0,
        to: 1,
        color: "#82cf88",
      },
    ],
    title: null,
  },
  series: [
    {
      data: [
        {
          y: apdex_dato,
          target: 1,
          color: "#3b5998",
        },
      ],
    },
  ],
  tooltip: {
    pointFormat: "<b>{point.y}</b>",
  },
});

const GraphApdexInner = ({ location }) => {
  const { t } = useTranslation();
  const initialObjectiveId = location?.query?.objetivoId || "";
  const [objectiveId, setObjectiveId] = useState(initialObjectiveId);
  const {
    data: objectives,
    isLoading: isLoadingObjectiveList,
  } = useObjectivesList({ fromStorage: true });
  const [globalPeriodValue, setGlobalPeriodValue] = useState("d");
  const [globalStatePeriod, setGlobalStatePeriod] = useState(
    defaultSelectDateRange
  );

  const {
    data: indicadoresApdex,
    isLoading: isLoadingIndicadoresApdex,
    isError: isErrorIndicadorApdex,
    error: errorIndicadorApdex,
  } = useIndicadorApdex({
    objetivoId: objectiveId,
    periodo: globalPeriodValue,
    globalStatePeriod: globalStatePeriod,
  });
  const [infoTarget, setInfoTarget] = useState(null);

  function handleChangeObjective(newValue) {
    setObjectiveId(newValue);
  }

  function changeGlobalPeriod({ period, statePeriod }) {
    setGlobalPeriodValue(period);
    setGlobalStatePeriod(statePeriod);
  }

  function getColor(apdex, { sla_error_apdex, sla_ok_apdex }) {
    if (apdex >= sla_ok_apdex) return "#9c9c9c";
    if (apdex <= sla_error_apdex) return "#e87482";
    return "#dbc670";
  }

  function onClickInfo(event) {
    setInfoTarget(event.currentTarget);
  }

  function onCloseInfo() {
    setInfoTarget(null);
  }

  return (
    <Card>
      <CardContent>
        <Box style={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h4">{(t && typeof t === 'function') ? (t('apdex.header.title') || 'Dashboard Apdex') : 'Dashboard Apdex'}</Typography>
            <Typography variant="body1">{(t && typeof t === 'function') ? (t('formRegistros.info.selectObjective') || 'Selecciona un objetivo') : 'Selecciona un objetivo'}</Typography>
          </Box>
          <Tooltip title={(t && typeof t === 'function') ? (t('apdex.popover.title') || '¿Qué es ApDex?') : '¿Qué es ApDex?'}>
            <IconButton onClick={onClickInfo}>
              <HelpIcon style={{ color: "#7786fc" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
      <Box padding={2}>
        <ApdexConfigWarning objetivoId={objectiveId} />
        <Grid container spacing={2}>
          <Grid item xl={4} lg={4} md={6} sm={12}>
            <ObjectiveSelect
              objetivos={objectives}
              objetivoId={objectiveId}
              setObjetivoId={handleChangeObjective}
              isLoading={isLoadingObjectiveList}
            />
            {isLoadingObjectiveList && (
              <Typography variant="body2" style={{ marginTop: 5 }}>
                {(t && typeof t === 'function') ? (t('apdex.loadingObjectives') || 'Cargando objetivos...') : 'Cargando objetivos...'}
              </Typography>
            )}
          </Grid>
          <Grid item xl={8} lg={8} md={6} sm={12}>
            <AtSelectPeriod
              persistInStorage={false}
              hideApplyToAll={true}
              changePeriodFn={changeGlobalPeriod}
            />
          </Grid>
          {isErrorIndicadorApdex && objectiveId !== "" ? (
            <Grid item lg={12} md={12} sm={12}>
              <Alert severity="info">
                <Typography>
                    {errorIndicadorApdex} {" "}
                    <Link
                      to={{
                        pathname: "manageObjectives",
                        search: getQueryParams(),
                        query: { tab: 4, objetivoId: objectiveId },
                      }}
                    >
                      {errorIndicadorApdex === "Umbrales no configurados." && (
                        <b>{(t && typeof t === 'function') ? (t('apdex.graph.alert.link') || 'Ir a configuración Apdex') : 'Ir a configuración ApDex'}</b>
                      )}
                    </Link>
                </Typography>
              </Alert>
            </Grid>
          ) : null}
          {isLoadingIndicadoresApdex && (
        <Grid lg={12} md={12} sm={12} style={{fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif', height: '85px', fontSize: '14px', color: '#666666', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Grid item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '24px', padding: "0.5rem" }} >
                  <CircularProgress variant="indeterminate" size={24} thickness={4} />
              </Grid>
              <Grid item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '24px', padding: "0.5rem" }} >
          {(t && typeof t === 'function') ? (t('apdex.graph.loading') || 'Cargando Indicador Apdex') : 'Cargando Indicador Apdex'}
              </Grid>
          </Grid>
          )}
          {indicadoresApdex && (
            <>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {indicadoresApdex.map(
                  ({
                    apdex_dato,
                    nombre_paso,
                    umbrales,
                    cantidad_tiempos: {
                      insatisfactorio,
                      satisfactorio,
                      tolerable,
                    },
                  }) => {
                    return (
                      <div key={nombre_paso} style={{ marginTop: "20px" }}>
                        {apdex_dato != null ? (
                          <Grid container>
                            <Grid item lg={3} md={3} sm={12} xs={12}>
                              <Card
                                style={{
                                  backgroundColor: "#f1f1f1",
                                  minWidth: "100%",
                                }}
                              >
                                <CardContent>
                                  <Typography variant="h5">
                                    {nombre_paso}
                                  </Typography>
                                  <Box
                                    style={{
                                      marginTop: "15px",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box style={{ marginRight: "20px" }}>
                                      <Typography
                                        variant="h1"
                                        style={{
                                          fontSize: "53px",
                                          color: getColor(apdex_dato, umbrales),
                                        }}
                                      >
                                        {!apdex_dato
                                          ? "0.000"
                                          : parseFloat(apdex_dato).toFixed(3)}
                                      </Typography>
                                    </Box>
                                    <Box marginLeft="18px">
                                      <Typography
                                        style={{ fontWeight: "bold" }}
                                      >
                                        Muestras
                                      </Typography>
                                      <Typography variant="body2">{(t && typeof t === 'function') ? (t('apdex.graph.card.satisfied') || 'Satisfactorio') : 'Satisfactorio'}: {" "}
                                        <span style={{ fontWeight: "bold" }}>
                                          {satisfactorio}
                                        </span>
                                      </Typography>
                                      <Typography variant="body2">{(t && typeof t === 'function') ? (t('apdex.graph.card.tolerating') || 'Tolerable') : 'Tolerable'}: {" "}
                                        <span style={{ fontWeight: "bold" }}>
                                          {tolerable}
                                        </span>
                                      </Typography>
                                      <Typography variant="body2">{(t && typeof t === 'function') ? (t('apdex.graph.card.frustrated') || 'Frustrado') : 'Frustrado'}: {" "}
                                        <span style={{ fontWeight: "bold" }}>
                                          {insatisfactorio}
                                        </span>
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item lg={9} md={9} sm={12} xs={12}>
                              <Box
                                style={{
                                  marginTop: "20px",
                                }}
                              >
                                <HighchartsReact
                                  highcharts={Highcharts}
                                  options={chartOptions({
                                    apdex_dato,
                                    nombre_paso,
                                    umbrales,
                                  })}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        ) : (
                          <Alert severity="info">
                            <Typography>
                              {(t && typeof t === 'function') ? (t('apdex.graph.alert.threshold-not-configured') || `Umbrales del paso `) : 'Umbrales del paso '}<b>{nombre_paso}</b>{' '}{(t && typeof t === 'function') ? (t('apdex.graph.alert.threshold-not-configured.suffix') || 'aún no han sido configurados') : 'aún no han sido configurados'}
                            </Typography>
                          </Alert>
                        )}
                      </div>
                    );
                  }
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      <Popover
        anchorEl={infoTarget}
        open={Boolean(infoTarget)}
        onClose={onCloseInfo}
        PaperProps={{
          style: { width: 350, backgroundColor: "rgba(70, 70, 70, .9)" },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box padding={2}>
          <Typography variant="body2" style={{ color: "white" }}>
            {(t && typeof t === 'function') ? (t('apdex.popover.content.text1') || 'Apdex es una medición numérica de la satisfacción del cliente con el rendimiento de aplicaciones de empresa. Asocia un número importante de mediciones en un número en una escala uniforme de 0 a 1 (0 = no hay usuarios satisfechos, 1 = todos los usuarios satisfechos).') : 'Apdex es una medición numérica de la satisfacción del cliente con el rendimiento de aplicaciones de empresa. Asocia un número importante de mediciones en un número en una escala uniforme de 0 a 1 (0 = no hay usuarios satisfechos, 1 = todos los usuarios satisfechos).'}
          </Typography>
          <Typography variant="body2" style={{ color: "white" }}>
            {(t && typeof t === 'function') ? (t('apdex.popover.content.text2') || 'Existen tres tipos de rendimiento, como se indica a continuación:') : 'Existen tres tipos de rendimiento, como se indica a continuación:'}
          </Typography>
          <Box marginTop={2}>
            <Typography variant="body2" style={{ color: "white" }}>
              <b>{(t && typeof t === 'function') ? (t('apdex.popover.content.satisfied') || 'Satisfecho:') : 'Satisfecho:'}</b>{(t && typeof t === 'function') ? (t('apdex.popover.content.satisfied.description') || ' el tiempo de respuesta real de la aplicación es inferior o igual al umbral de Apdex.') : ' el tiempo de respuesta real de la aplicación es inferior o igual al umbral de Apdex.'}
            </Typography>
            <Typography variant="body2" style={{ color: "white" }}>
              <b>{(t && typeof t === 'function') ? (t('apdex.popover.content.tolerating') || 'Tolerable:') : 'Tolerable:'}</b>{(t && typeof t === 'function') ? (t('apdex.popover.content.tolerating.description') || ' el tiempo de respuesta real de la aplicación es mayor que el umbral de Apdex, pero inferior o igual a cuatro veces el umbral de Apdex.') : ' el tiempo de respuesta real de la aplicación es mayor que el umbral de Apdex, pero inferior o igual a cuatro veces el umbral de Apdex.'}
            </Typography>
            <Typography variant="body2" style={{ color: "white" }}>
              <b>{(t && typeof t === 'function') ? (t('apdex.popover.content.frustrated') || 'Frustrante:') : 'Frustrante:'}</b>{(t && typeof t === 'function') ? (t('apdex.popover.content.frustrated.description') || ' el tiempo real de respuesta de la aplicación es superior a el umbral de Apdex.') : ' el tiempo real de respuesta de la aplicación es superior a el umbral de Apdex.'}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </Card>
  );
}

export const GraphApdex = withRouter(GraphApdexInner);
